<template>
    <div class="summary-tab p-3">
        <!-- Başarı Durumu (Success State) -->
        <div v-if="isSuccess" class="success-container text-center py-5">
            <div class="mb-4">
                <div class="success-icon-wrapper shadow-lg mb-4">
                    <i class="ri-checkbox-circle-fill text-success display-2"></i>
                </div>
                <h2 class="font-weight-bold text-dark mb-3">{{ $t('application_success_title') }}</h2>
                <p class="text-muted font-size-16 mb-4 px-md-5">
                    {{ $t('application_success_description') }}
                </p>
            </div>
        </div>
        <!-- Yükleme Durumu -->
        <div v-else-if="loading" class="text-center py-4">
            <b-spinner small variant="primary"></b-spinner>
            <span class="ml-2">{{ $t('loading') }}</span>
        </div>
        <!-- Form ve Özet Durumu -->
        <div v-else>
            <b-row class="mb-4">
                <!-- 1- Bilgiler -->
                <b-col md="6" class="mb-3">
                    <b-card no-body class="h-100 shadow-sm border">
                        <b-card-header class="font-weight-bold bg-light">
                            {{ $t('application_info_title') }}
                        </b-card-header>
                        <b-card-body class="font-size-13 text-muted">
                            <template>
                                <div><strong>{{ $t('full_name') }}:</strong> {{ summaryData.name }} {{ summaryData.surname }}</div>
                                <div><strong>{{ $t('tc_no')}}:</strong> {{ summaryData.national_id}}</div>
                                <div><strong>{{ $t('email') }}:</strong> {{summaryData.email }}</div>
                                <div><strong>{{ $t('telephone') }}:</strong> {{ summaryData.phone }}</div>
                            </template>
                        </b-card-body>
                    </b-card>
                </b-col>

                <!-- 2- Program Seçimi -->
                <b-col md="6" class="mb-3">
                    <b-card no-body class="h-100 shadow-sm border">
                        <b-card-header class="font-weight-bold bg-light">
                            {{ $t('program_selection_title') }}
                        </b-card-header>
                        <b-card-body class="font-size-13 text-muted">
                            <template>
                                <div><strong>{{ $t('faculty') }}:</strong> {{ summaryData.bau_faculty_name || '-' }}</div>
                                <div><strong>{{ $t('department') + '-' + $t('program') }}:</strong> {{ getLocaleText(summaryData,'bau_department_name') + ' - '+  getLocaleText(summaryData,'bau_program_name') }}</div>
                                <div><strong>{{ $t('program_level') }}:</strong> {{ switchProgramLevel(summaryData.target_program_level)}}</div>
                            </template>
                        </b-card-body>
                    </b-card>
                </b-col>

                <!-- 3- Ders Kayıt -->
                <b-col md="6" class="mb-3">
                    <b-card no-body class="h-100 shadow-sm border">
                        <b-card-header class="font-weight-bold bg-light">
                            {{ $t('course_registration_title') }}
                        </b-card-header>
                        <b-card-body class="font-size-13 text-muted">
                            <ul class="pl-3 mb-0" v-if="summaryData.courses && summaryData.courses.length">
                                <li v-for="(course, idx) in summaryData.courses" :key="idx">
                                    {{ course.course_code || course.code }} - {{ course.course_name || course.name }} ({{ course.credits || course.credit }} Kredi)
                                </li>
                            </ul>
                            <div v-else>{{ $t('no_course_added') }}</div>
                        </b-card-body>
                    </b-card>
                </b-col>

                <b-col md="6" class="mb-3">
                    <b-card no-body class="h-100 shadow-sm border">
                        <b-card-header class="font-weight-bold bg-light">
                            {{ $t('documents_title') }}
                        </b-card-header>
                        <b-card-body class="font-size-13 text-muted">
                            <ul class="pl-3 mb-0" v-if="summaryData.documents && summaryData.documents.length">
                                <li v-for="(doc, idx) in summaryData.documents" :key="idx">
                                    {{ getLocaleText(doc,'document_type_name') }}
                                    <i v-if="doc.status==='uploaded'" class="ri-check-line text-success font-weight-bold ml-1"></i>
                                    <span v-else class="text-secondary small"> - {{ $t('not_uploaded') }}</span>
                                </li>
                            </ul>
                            <div v-else>{{ $t('no_document_uploaded') }}</div>
                        </b-card-body>
                    </b-card>
                </b-col>
            </b-row>

            <div v-if="['waiting'].includes(summaryData.application_status)">
                <h5 class="font-weight-bold mb-3">{{ $t('information_text') }}</h5>
                <div class="border p-3 rounded bg-light font-size-12 text-justify mb-4" style="max-height: 250px; overflow-y: auto;">
                    <p class="font-weight-bold mb-1" v-html="$t('special_student_general_rule')"></p>

                    <template>
                        <p class="font-weight-bold mt-3 mb-1" v-html="$t('special_student_grad_title')"></p>
                        <p v-html="$t('special_student_grad_body')"></p>
                    </template>
                </div>

                <b-form-group class="mb-3">
                    <b-form-checkbox v-model="agreements.rules">{{ $t('special_student_rules_agreement') }}</b-form-checkbox>
                </b-form-group>
                <b-form-group class="mb-5">
                    <b-form-checkbox v-model="agreements.kvkk">{{ $t('special_student_kvkk_agreement') }}</b-form-checkbox>
                </b-form-group>
                <div class="d-flex flex-column flex-sm-row justify-content-center align-items-center pb-5">
                    <b-button variant="outline-primary" @click="$emit('prev')" class="w-100 w-sm-auto px-4 font-weight-bold shadow-sm mb-3 mb-sm-0 mr-sm-3">
                        <i class="ri-arrow-left-line mr-1"></i> {{ $t('back') }}
                    </b-button>
                    <b-button variant="primary" @click="submitApplication" :disabled="!isFormValid || submitting" class="w-100 w-sm-auto px-5 font-weight-bold shadow-sm font-size-15">
                        <b-spinner small v-if="submitting" class="mr-2"></b-spinner>
                        {{ $t('application_send') }}
                    </b-button>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import SpecialStudentApplicationRegisterServices from "@/services/SpecialStudentApplicationRegisterServices";

export default {
    name: "SummaryTab",
    data() {
        return {
            loading: false,
            submitting: false,
            isSuccess: false,
            summaryData: null,
            agreements: {
                rules: false,
                kvkk: false
            }
        }
    },
    computed: {
        isFormValid() {
            return this.agreements.rules && this.agreements.kvkk;
        },
    },
    created() {
        this.fetchSummary();
    },
    methods: {
        switchProgramLevel(level){
            if (level==='OL'){
                return this.$t("program_level_ol")
            }else if (level==='L'){
                return this.$t("program_level_l")
            }else if (level==='YL'){
                return this.$t("program_level_yl")
            }else if (level==='D'){
                return  this.$t('program_level_d')
            }
        },
        async fetchSummary() {
            this.loading = true;
            try {
                const specialData = this.getWithExpiry('specialStudentData', true);
                if (!specialData) return;
                const payload = {
                    national_id: specialData.national_id || null,
                    passport_number: specialData.passport_number || null,
                    pin: specialData.pin
                };
                const response = await SpecialStudentApplicationRegisterServices.summary(payload);
                this.summaryData = response.data.data ;
            } catch (error) {
                this.showErrors(error,null);
            } finally {
                this.loading = false;
            }
        },
        async submitApplication() {
            this.submitting = true;
            try {
                const data = {
                    'national_id': this.summaryData.national_id,
                    'passport_number': this.summaryData.passport_number,
                    'pin': this.summaryData.pin,
                    'is_information_notice_approved': this.agreements.rules,
                    'is_kvkk_approved': this.agreements.kvkk,
                }
                await SpecialStudentApplicationRegisterServices.makeApplication(data);
                this.$toast.success(this.$t('application_save_success'));
                this.isSuccess = true;
                setTimeout(()=>{
                    this.$store.dispatch('SpecialStudentApplicationRegister/logout')
                    this.$router.push('/special-student/application/login');
                },5000)
            } catch (error) {
                this.showErrors(error,null);
            } finally {
                this.submitting = false;
            }
        },
        goToLogin() {
            this.$router.push('/special-student/application/login');
        }
    }
}
</script>

<style scoped>
.success-container {
    background: linear-gradient(180deg, #f0fff4 0%, #ffffff 100%);
    border-radius: 20px;
    margin: 20px 0;
    border: 1px solid #e6ffed;
}

.success-icon-wrapper {
    width: 100px;
    height: 100px;
    background: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    box-shadow: 0 10px 25px rgba(40, 167, 69, 0.15);
    animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes scaleIn {
    from {
        transform: scale(0);
        opacity: 0;
    }
    to {
        transform: scale(1);
        opacity: 1;
    }
}

.font-size-13 { font-size: 13px; }
.font-size-15 { font-size: 15px; }
.font-size-16 { font-size: 16px; }
.font-weight-medium { font-weight: 500; }
</style>
